/**
 * Keep a main chat `?focus=<message_id>` link alive across the login
 * redirect.
 *
 * Push notifications and automation emails link to `/?focus=<id>`. Keycloak's
 * normal login round trip keeps the query, but its recovery path
 * (`redirectToCleanUrl` in keycloak.ts) rebuilds the URL from origin and path
 * only, so the focus would be lost. `stashPendingFocus()` runs at the top of
 * `initKeycloak()`, before any redirect, and the MainThread page reads the
 * query first and the stash second (`consumePendingFocus`).
 *
 * All storage access is wrapped in try/catch: a private window or blocked
 * site data must never break login.
 */

export const PENDING_FOCUS_KEY = 'andi.pendingFocus';

/** A stash older than this is ignored (the user wandered off). */
export const PENDING_FOCUS_MAX_AGE_MS = 10 * 60 * 1000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value is a message id (a UUID). */
export function isMessageId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Whether a path renders the main chat (`/` or `/main...`). */
export function isMainThreadPath(pathname: string): boolean {
  return pathname === '/' || pathname === '' || pathname.startsWith('/main');
}

/** Stash the current URL's `focus` when it targets the main chat. */
export function stashPendingFocus(
  location: Pick<Location, 'pathname' | 'search'> = window.location
): void {
  try {
    if (!isMainThreadPath(location.pathname)) return;
    const focus = new URLSearchParams(location.search).get('focus');
    if (!isMessageId(focus)) return;
    sessionStorage.setItem(PENDING_FOCUS_KEY, JSON.stringify({ id: focus, at: Date.now() }));
  } catch {
    // Storage blocked: the URL's own query is the only source then.
  }
}

/**
 * The focus to honor on the main chat: the URL's `focus` when it is a
 * message id, else a stash younger than ten minutes. The stash is cleared
 * either way, so it is honored at most once.
 */
export function consumePendingFocus(search: string, now: number = Date.now()): string | null {
  let stashed: string | null = null;
  try {
    const raw = sessionStorage.getItem(PENDING_FOCUS_KEY);
    sessionStorage.removeItem(PENDING_FOCUS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown } | null;
      if (
        parsed &&
        isMessageId(typeof parsed.id === 'string' ? parsed.id : null) &&
        typeof parsed.at === 'number' &&
        now - parsed.at >= 0 &&
        now - parsed.at < PENDING_FOCUS_MAX_AGE_MS
      ) {
        stashed = parsed.id as string;
      }
    }
  } catch {
    stashed = null;
  }
  const fromUrl = new URLSearchParams(search).get('focus');
  if (isMessageId(fromUrl)) return fromUrl;
  return stashed;
}
